<script setup lang="ts">
import { motion } from 'motion-v'
import type { IntRange } from 'type-fest'

const { t, tm, rt } = useI18n()

function getInquiryType(index: StringifiedInt<IntRange<0, 4>>) {
  return match(index)
    .with('0', () => 'free-demo' as const)
    .with('1', () => 'existing-customer' as const)
    .with('2', () => 'partner-up' as const)
    .with('3', () => 'question' as const)
    .exhaustive()
}
</script>

<template>
  <section class="mx-auto max-w-screen-xl px-4 py-5 lg:px-8">
    <UiSectionHeadline
      :title="t('contact_us.title')"
      :subtitle="t('contact_us.subtitle')"
    />

    <motion.div
      class="mx-auto mt-10 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4"
      :variants="{
        initial: { opacity: 0 },
        animate: { opacity: 1 }
      }"
      initial="initial"
      while-in-view="animate"
      exit="initial"
    >
      <motion.div
        v-for="(item, itemIndex) in tm('contact_us.items')"
        :key="itemIndex"
        :variants="{
          initial: { y: 50 },
          animate: {
            y: 0,
            transition: {
              type: 'spring',
              stiffness: 100,
              damping: 10,
              delay: Number(itemIndex) * 0.1
            }
          }
        }"
        initial="initial"
        while-in-view="animate"
        exit="initial"
        class="group relative grid grid-flow-row grid-rows-[auto_1fr] gap-5 overflow-clip rounded-3xl border border-neutral-200 bg-white p-5"
      >
        <div class="relative w-full select-none">
          <i18n-t
            :keypath="`contact_us.items.${itemIndex}.title`"
            tag="p"
            class="flex flex-col text-base"
          >
            <template #title_placeholder>
              <span class="text-xl font-medium leading-5 text-orange-500">
                {{ rt(item.title_placeholder) }}
              </span>
            </template>
          </i18n-t>
        </div>

        <div class="flex grow flex-col justify-between gap-y-5 text-left text-base">
          <p class="text-gray-600">
            {{ rt(item.description) }}
          </p>

          <UiButton
            :color="itemIndex === '0' ? 'accent' : 'neutral'"
            class="w-full"
          >
            {{ rt(item.action) }}

            <UiIcon
              name="arrow-forward-rounded"
              class="hidden transition-transform duration-200 group-hover:translate-x-1 md:visible md:block"
            />
          </UiButton>
        </div>

        <RouterLink
          :to="{
            name: `/contact-us/${getInquiryType(itemIndex)}`,
          }"
          class="absolute inset-0"
        />
      </motion.div>
    </motion.div>
  </section>
</template>
